/* eslint-disable no-unused-vars */
import React, { useEffect, useRef } from 'react';
import {
  useFormContext,
  Controller,
  UseControllerProps,
} from 'react-hook-form';
import Checkbox, { CheckboxProps } from '@mui/material/Checkbox';
import FormControlLabel, {
  FormControlLabelProps,
} from '@mui/material/FormControlLabel';

type FormCheckboxProps = UseControllerProps & {
  inner_props?: {
    form_control_label_props?: Omit<FormControlLabelProps, 'control'>;
    checkbox_props?: CheckboxProps;
  };
};

const createSyntheticEvent = <T extends Element, E extends Event>(
  event: E
): React.SyntheticEvent<T, E> => {
  let isDefaultPrevented = false;
  let isPropagationStopped = false;
  const preventDefault = () => {
    isDefaultPrevented = true;
    event.preventDefault();
  };
  const stopPropagation = () => {
    isPropagationStopped = true;
    event.stopPropagation();
  };
  return {
    nativeEvent: event,
    currentTarget: event.currentTarget as EventTarget & T,
    target: event.target as EventTarget & T,
    bubbles: event.bubbles,
    cancelable: event.cancelable,
    defaultPrevented: event.defaultPrevented,
    eventPhase: event.eventPhase,
    isTrusted: event.isTrusted,
    preventDefault,
    isDefaultPrevented: () => isDefaultPrevented,
    stopPropagation,
    isPropagationStopped: () => isPropagationStopped,
    persist: () => {},
    timeStamp: event.timeStamp,
    type: event.type,
  };
};

export default function FormCheckbox(props: FormCheckboxProps) {
  const { control } = useFormContext();
  const { name } = props;

  return (
    <Controller
      name={name}
      control={control}
      rules={props.rules}
      defaultValue={false}
      render={({ field }) => {
        const [checked, setChecked] = React.useState(
          props.inner_props?.checkbox_props?.checked || false
        );
        const cbxRef = useRef<HTMLInputElement>(null);

        useEffect(() => {
          if (props.inner_props?.checkbox_props?.checked !== undefined) {
            setChecked(props.inner_props.checkbox_props.checked);

            // Приходится тригерить синтетическое событие на чекбоксе
            if (cbxRef.current) {
              cbxRef.current.checked = props.inner_props.checkbox_props.checked;

              const event = new Event('change', { bubbles: true });
              Object.defineProperty(event, 'target', {
                writable: false,
                value: cbxRef.current,
              });

              const syntheticEvent = createSyntheticEvent(
                event
              ) as React.ChangeEvent<HTMLInputElement>;
              onChange(syntheticEvent);
            }
          }
        }, [props.inner_props?.checkbox_props?.checked]);

        const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          setChecked(e.target.checked);
          field.onChange(e, checked);

          if (props.inner_props?.checkbox_props?.onChange !== undefined) {
            props.inner_props.checkbox_props.onChange(e, checked);
          }
        };

        return (
          <FormControlLabel
            label="Запомнить меня"
            {...props.inner_props?.form_control_label_props}
            control={
              <Checkbox
                {...field}
                {...props.inner_props?.checkbox_props}
                checked={checked}
                onChange={onChange}
                inputRef={cbxRef}
              />
            }
          ></FormControlLabel>
        );
      }}
    />
  );
}
